import React, { useState, useEffect } from "react";
import axios from "axios";
import "./admin.css";

function EditSlots() {
  const renderCategory = (categoryKey, title, placeholder) => (
    <div className="category text-start mt-4">
      <h2>{title}</h2>
      <div className="form-group">
        <label>Add New Slot</label>
        <input
          type="text"
          value={newSlotInput[categoryKey]}
          onChange={(e) =>
            setNewSlotInput((prev) => ({
              ...prev,
              [categoryKey]: e.target.value,
            }))
          }
          className="form-control"
          placeholder={placeholder}
        />
        <button
          onClick={() => handleAddSlot(categoryKey)}
          className="btn btn-success mt-2"
          disabled={!newSlotInput[categoryKey].trim()}
        >
          Add Slot
        </button>
      </div>
    </div>
  );

  const [slots, setSlots] = useState({
    category1: [],
    category2: [],
    category3: [],
  });

  const [newSlotInput, setNewSlotInput] = useState({
    category1: "",
    category2: "",
    category3: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 25;

  // Store locked slot names (normalized to uppercase)
  const [lockedSlots, setLockedSlots] = useState(new Set());

  useEffect(() => {
    const fetchSlots = async () => {
      try {
        const [category1Res, category2Res, category3Res] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BASE_API_URL}/twoWheelerSlots/api/get`),
          axios.get(`${process.env.REACT_APP_BASE_API_URL}/lmvSlots/api/get`),
          axios.get(`${process.env.REACT_APP_BASE_API_URL}/hmvSlots/api/get`),
        ]);

        const category1Slots = Array.isArray(category1Res.data.data)
          ? category1Res.data.data.map((slot) => ({
            _id: slot._id || slot.id,
            name: slot.slots,
            status: slot.status || "available",
          }))
          : [];

        const category2Slots = Array.isArray(category2Res.data.data)
          ? category2Res.data.data.map((slot) => ({
            _id: slot._id || slot.id,
            name: slot.slots || slot,
          }))
          : [];

        const category3Slots = Array.isArray(category3Res.data.data)
          ? category3Res.data.data.map((slot) => ({
            _id: slot._id || slot.id,
            name: slot.slots || slot,
          }))
          : [];

        setSlots({
          category1: category1Slots,
          category2: category2Slots,
          category3: category3Slots,
        });
      } catch (error) {
        console.error("Error fetching slots:", error);
        alert("Failed to load parking slots.");
      }
    };

    fetchSlots();
  }, []);

  // Fetch locked slots from all vehicle parking types
  useEffect(() => {
    const fetchLockedSlots = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/vehicle/api/allVehicleList`
        );

        // Combine all parking types
        const dayParking = res.data.dayParking || [];
        const hourlyParking = res.data.hourlyParking || [];
        const monthlyParking = res.data.monthlyParking || [];
        const allVehicles = [...dayParking, ...hourlyParking, ...monthlyParking];

        // Extract parking slots and normalize to uppercase for matching
        const occupiedSet = new Set(
          allVehicles
            .map((v) => v.parkingSlot)
            .filter((slot) => slot) // Remove null/undefined
            .map((slot) => slot.trim().toUpperCase())
        );

        console.log("Locked Slots:", Array.from(occupiedSet)); // Debug
        setLockedSlots(occupiedSet);
      } catch (e) {
        console.error("Error fetching vehicles: ", e);
      }
    };
    fetchLockedSlots();
  }, []);

  const handleAddSlot = async (category) => {
    const newSlot = newSlotInput[category].trim();
    if (newSlot === "") return;

    let endpoint = "";
    if (category === "category1") {
      endpoint = `${process.env.REACT_APP_BASE_API_URL}/twoWheelerSlots/api/create`;
    } else if (category === "category2") {
      endpoint = `${process.env.REACT_APP_BASE_API_URL}/lmvSlots/api/create`;
    } else if (category === "category3") {
      endpoint = `${process.env.REACT_APP_BASE_API_URL}/hmvSlots/api/create`;
    }

    try {
      const response = await axios.post(endpoint, { slots: newSlot });
      if (response.status === 201) {
        let created = response.data.newSlot || response.data.data || {};
        const addedSlot = {
          _id: created._id || created.id,
          name: created.slots || newSlot,
          status: created.status || "available",
        };
        setSlots((prev) => ({
          ...prev,
          [category]: [...prev[category], addedSlot],
        }));
        setNewSlotInput((prev) => ({ ...prev, [category]: "" }));
      }
    } catch (error) {
      console.error("Error adding slot:", error);
      alert("Error adding slot. Please try again.");
    }
  };

  const handleRemoveSlot = async (category, index, id) => {
    try {
      if (!id) {
        alert("No ID provided for deletion.");
        return;
      }

      const endpoints = {
        category1: `${process.env.REACT_APP_BASE_API_URL}/twoWheelerSlots/api/deleteSlots/${id}`,
        category2: `${process.env.REACT_APP_BASE_API_URL}/lmvSlots/api/deleteSlots/${id}`,
        category3: `${process.env.REACT_APP_BASE_API_URL}/hmvSlots/api/deleteSlots/${id}`,
      };

      await axios.delete(endpoints[category]);

      setSlots((prev) => {
        const updated = { ...prev };
        updated[category] = [...updated[category]];
        updated[category].splice(index, 1);
        return updated;
      });
    } catch (err) {
      console.error("Error deleting slot:", err);
      alert("Failed to delete slot from DB.");
    }
  };

  const paginateData = (data) => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const renderAvailableSlotsTable = () => {
    const availableSlots = [];

    slots.category1.forEach((slot, index) => {
      if (slot.status === "available") {
        availableSlots.push({
          category: "Bike / Scooter",
          name: slot.name,
          categoryKey: "category1",
          index,
          id: slot._id,
        });
      }
    });

    slots.category2.forEach((slot, index) => {
      availableSlots.push({
        category: "Car / LMV",
        name: slot.name,
        categoryKey: "category2",
        index,
        id: slot._id,
      });
    });

    slots.category3.forEach((slot, index) => {
      availableSlots.push({
        category: "Bus / Truck / HMV",
        name: slot.name || slot,
        categoryKey: "category3",
        index,
        id: slot._id,
      });
    });

    if (availableSlots.length === 0) {
      return <p className="mt-4">No available slots to show.</p>;
    }

    const paginatedData = paginateData(availableSlots);
    const totalPages = Math.ceil(availableSlots.length / rowsPerPage);

    return (
      <div className="container mt-5">
        <div className="card shadow">
          <div className="card-header bg-warning text-white">
            <h4 className="mb-0">All Available Slots</h4>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead className="thead-dark">
                  <tr>
                    <th scope="col">Category</th>
                    <th scope="col">Slot Name</th>
                    <th scope="col">Status</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((slot, i) => {
                    // Normalize slot name to uppercase for matching
                    const slotNameNormalized = slot.name.trim().toUpperCase();
                    const isLocked = lockedSlots.has(slotNameNormalized);

                    if (isLocked) {
                      console.log(`🔒 Slot "${slot.name}" is locked.`); // Debug
                    }

                    return (
                      <tr key={i}>
                        <td>{slot.category}</td>
                        <td>{slot.name}</td>
                        <td style={{ textAlign: "center" }}>
                          {isLocked ? (
                            <span
                              style={{ fontSize: "20px", color: "red" }}
                              title="Slot is currently occupied"
                            >
                              🔒
                            </span>
                          ) : (
                            <span
                              style={{ fontSize: "20px", color: "green" }}
                              title="Slot is available"
                            >
                              ✓
                            </span>
                          )}
                        </td>
                        <td>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() =>
                              handleRemoveSlot(
                                slot.categoryKey,
                                slot.index,
                                slot.id
                              )
                            }
                            disabled={isLocked}
                            title={isLocked ? "Cannot delete - Slot is occupied" : "Delete this slot"}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage((prev) => prev - 1)}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {Array.from({ length: totalPages }).map((_, i) => (
                            <li
                              key={i}
                              className={`page-item ${currentPage === i + 1 ? "active" : ""
                                }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage(i + 1)}
                              >
                                {i + 1}
                              </button>
                            </li>
                          ))}
                          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage((prev) => prev + 1)}
                              disabled={currentPage === totalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="editSlots container">
      <h1 className="my-4">Parking Slots Editing Section</h1>
      {/* Category Inputs */}
      <div>
        {renderCategory("category1", "Bike / Scooter", "Enter Slot Name (e.g., A1)")}
        {renderCategory("category2", "Car / LMV", "Enter Slot Name (e.g., B1)")}
        {renderCategory("category3", "Bus / Truck / HMV", "Enter Slot Name (e.g., C1)")}
      </div>
      {/* Table */}
      {renderAvailableSlotsTable()}
    </div>
  );
}

export default EditSlots;
